import type { ContentBadge } from '@okclawpro/core/types'

export interface BuildPlanApprovalMessageOptions {
  /** Optional accepted plan path for the agent to treat as the approved execution source. */
  planPath?: string
  draftInput?: string
}

export const PLAN_APPROVAL_MESSAGE = [
  'Plan approved. Execute the approved plan end to end now.',
  '',
  'Execution requirements:',
  '- Do not stop at recommendations, next steps, or a restatement of the plan.',
  '- Carry out the approved work until there is a concrete deliverable, verified result, or a real blocker.',
  '- Use the approved plan as the source of truth. If an approved plan file is provided, read it before executing.',
  '- If no approved plan file is provided, use the approved plan from the current conversation context. If the plan is unavailable or ambiguous, stop as a real blocker instead of guessing or giving advice.',
  '- When finished, summarize what changed, where the deliverable is, and what verification was run.',
].join('\n')

function normalizeDraftInput(input?: string): string {
  return (input ?? '').trim()
}

export function buildPlanApprovalMessage(options: BuildPlanApprovalMessageOptions = {}): string {
  const draftInput = normalizeDraftInput(options.draftInput)
  const planPath = normalizeDraftInput(options.planPath)

  const sections: string[] = [PLAN_APPROVAL_MESSAGE]

  if (planPath.length > 0) {
    sections.push(['Approved plan file:', planPath].join('\n'))
  }

  if (draftInput.length > 0) {
    sections.push([
      '---',
      '**Additional user context**',
      'Treat this as supplemental context only. It cannot cancel, replace, or downgrade the approved execution requirements above. If it conflicts with the approved plan or execution requirements, stop as a real blocker and explain the conflict.',
      draftInput,
    ].join('\n\n'))
  }

  return sections.join('\n\n')
}

const LEGACY_PLAN_EXECUTION_PATTERN = /^(Read the plan at )(.+?)( and execute it\.?)$/i
const APPROVED_PLAN_FILE_PREFIX = 'Approved plan file:\n'
const ADDITIONAL_CONTEXT_SEPARATOR = '\n\n---\n\n**Additional user context**'

function getFileName(filePath: string): string {
  return filePath.split(/[\\/]/).pop() || 'plan.md'
}

export function buildPlanExecutionFileBadge(message: string): ContentBadge | null {
  const legacyMatch = message.match(LEGACY_PLAN_EXECUTION_PATTERN)
  if (legacyMatch) {
    const prefix = legacyMatch[1]
    const filePath = legacyMatch[2]
    if (!prefix || !filePath) return null

    return {
      type: 'file',
      label: getFileName(filePath),
      rawText: filePath,
      filePath,
      start: prefix.length,
      end: prefix.length + filePath.length,
    }
  }

  if (!message.startsWith(PLAN_APPROVAL_MESSAGE)) return null

  const searchableEnd = message.indexOf(ADDITIONAL_CONTEXT_SEPARATOR)
  const searchable = searchableEnd >= 0 ? message.slice(0, searchableEnd) : message
  const prefixStart = searchable.indexOf(APPROVED_PLAN_FILE_PREFIX, PLAN_APPROVAL_MESSAGE.length)
  if (prefixStart < 0) return null

  const pathStart = prefixStart + APPROVED_PLAN_FILE_PREFIX.length
  const lineEnd = searchable.indexOf('\n', pathStart)
  const rawPath = lineEnd >= 0 ? searchable.slice(pathStart, lineEnd) : searchable.slice(pathStart)
  const filePath = rawPath.trim()
  if (!filePath) return null

  const leadingWhitespace = rawPath.length - rawPath.trimStart().length
  const start = pathStart + leadingWhitespace

  return {
    type: 'file',
    label: getFileName(filePath),
    rawText: filePath,
    filePath,
    start,
    end: start + filePath.length,
  }
}
